import React, { memo, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Box } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useIntl } from 'react-intl';
import { OVERVIEW_UNITS_ENUM } from '~/modules/resource-management/common/enums';
import useResourceManagementContext from '~/modules/resource-management/common/context/useResourceManagementContext';
import useResourceOverviewToolbarContext from '~/modules/resource-management/ResourceManagementDashboard/hooks/useResourceManagementDashboardContext';
import useFilterBasedOnSearchCriteria from '~/modules/resourcing/hooks/useFilterBasedOnSearchCriteria';
import { compareDateObject } from '~/modules/common/dates/compare';
import { useBenchTimelineDateRange } from './hooks/useBenchTimelineDateRange';
import BenchOverAllocationTimelineSkeleton from './BenchOverAllocationTimelineSkeleton';
import PeriodBlock from './PeriodBlock';
import useBenchOverAllocationDataPoints from './hooks/useBenchOverAllocationDataPoints';

const useStyles = makeStyles(() => ({
  container: {
    display: 'flex',
    alignItems: 'flex-start',
    position: 'relative',
    width: '100%'
  }
}));

const isDateInRange = (date, range) => {
  if (!date || !range) return false;

  return date >= range.startDate && date <= range.endDate;
};

const BenchOverAllocationTimeline = ({
  selectedPeriod,
  onPeriodClick,
  showBenchDetailSection
}) => {
  const classes = useStyles();
  const { formatMessage } = useIntl();

  const {
    periodScale,
    displayUnit,
    dateRange
  } = useResourceOverviewToolbarContext();

  const { searchCriteria } = useResourceManagementContext();
  const { filter } = useFilterBasedOnSearchCriteria({ searchCriteria });

  const { startDate: toolbarStartDate } = dateRange;

  const [timelineStartDate, setTimelineStartDate] = useState(toolbarStartDate);

  const { dateRange: timelineDateRange } = useBenchTimelineDateRange({
    startDate: timelineStartDate,
    periodScale
  });

  const { loading, dataPoints, maxValue } = useBenchOverAllocationDataPoints({
    overViewUnit: displayUnit,
    dateRange: timelineDateRange,
    periodScale,
    filter
  });

  useEffect(() => {
    setTimelineStartDate(toolbarStartDate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [periodScale]);

  useEffect(() => {
    if (!isDateInRange(toolbarStartDate, timelineDateRange)) {
      setTimelineStartDate(toolbarStartDate);
    }
  }, [toolbarStartDate, timelineDateRange]);

  if (loading) {
    return <BenchOverAllocationTimelineSkeleton />;
  }

  if (!dataPoints || dataPoints.length === 0) {
    return null;
  }

  const unitLabel = formatMessage({
    id:
      displayUnit === OVERVIEW_UNITS_ENUM.HOURS
        ? 'resourceManagementDashboard.benchOverAllocationTracker.hours'
        : displayUnit === OVERVIEW_UNITS_ENUM.PERCENTAGE
        ? 'resourceManagementDashboard.benchOverAllocationTracker.percentage'
        : 'resourceManagementDashboard.benchOverAllocationTracker.resourcesFTE'
  });

  return (
    <Box
      role="region"
      aria-label={formatMessage({
        id:
          'resourceManagementDashboard.benchOverAllocationTracker.timelineLabel'
      })}
    >
      <Box className={classes.container}>
        {dataPoints.map(item => (
          <PeriodBlock
            key={item.period}
            data={item}
            isSelected={
              selectedPeriod &&
              compareDateObject(selectedPeriod, item.startDate) === 0
            }
            onClick={onPeriodClick}
            maxValue={maxValue}
            unitLabel={unitLabel}
            showBenchDetailSection={showBenchDetailSection}
          />
        ))}
      </Box>
    </Box>
  );
};

BenchOverAllocationTimeline.propTypes = {
  selectedPeriod: PropTypes.object,
  onPeriodClick: PropTypes.func.isRequired,
  showBenchDetailSection: PropTypes.bool.isRequired
};
export default memo(BenchOverAllocationTimeline);
